import { useState } from "react";
import { useNavigate } from "react-router-dom";

// ── Fondo con blobs y partículas animadas ────────────────────────────────────
function AmbientBG() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {/* Blobs de color */}
      <div style={{ position:"absolute", top:"8%", left:"10%", width:500, height:500, background:"radial-gradient(circle,rgba(99,102,241,0.14) 0%,transparent 70%)", borderRadius:"50%" }} />
      <div style={{ position:"absolute", bottom:"15%", right:"8%", width:420, height:420, background:"radial-gradient(circle,rgba(147,51,234,0.12) 0%,transparent 70%)", borderRadius:"50%" }} />
      <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:700, height:700, background:"radial-gradient(circle,rgba(45,212,191,0.04) 0%,transparent 70%)", borderRadius:"50%" }} />
      {/* Partículas SVG */}
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6%"  cy="18%" r="2"   fill="#a78bfa" opacity="0.5"><animate attributeName="cy" values="18%;28%;18%" dur="9s"  repeatCount="indefinite"/></circle>
        <circle cx="88%" cy="12%" r="1.5" fill="#f59e0b" opacity="0.5"><animate attributeName="cy" values="12%;22%;12%" dur="11s" repeatCount="indefinite"/></circle>
        <circle cx="78%" cy="68%" r="2"   fill="#34d399" opacity="0.4"><animate attributeName="cy" values="68%;78%;68%" dur="8s"  repeatCount="indefinite"/></circle>
        <circle cx="22%" cy="78%" r="1.5" fill="#a78bfa" opacity="0.4"><animate attributeName="cy" values="78%;88%;78%" dur="7s"  repeatCount="indefinite" begin="2s"/></circle>
        <circle cx="55%" cy="4%"  r="2.5" fill="#f59e0b" opacity="0.35"><animate attributeName="cy" values="4%;14%;4%" dur="12s" repeatCount="indefinite" begin="1s"/></circle>
        <circle cx="40%" cy="92%" r="2"   fill="#6366f1" opacity="0.3"><animate attributeName="cy" values="92%;82%;92%" dur="10s" repeatCount="indefinite" begin="3s"/></circle>
      </svg>
    </div>
  );
}

// ── Modal de formulario de contacto ─────────────────────────────────────────
function ContactModal({ onClose }) {
  const [form, setForm]       = useState({ nombre: "", contacto: "", email: "", descripcion: "", negocio: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState("");

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.nombre.trim() || form.nombre.trim().length < 2)
      return setError("Ingresa tu nombre completo.");
    if (!form.contacto.trim() || form.contacto.trim().length < 5)
      return setError("Ingresa un contacto válido (WhatsApp, teléfono, Instagram, etc.).");
    if (!form.descripcion.trim() || form.descripcion.trim().length < 10)
      return setError("La descripción debe tener al menos 10 caracteres.");
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return setError("El correo electrónico no es válido.");

    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/promo-contact`,
        {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(form),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Error al enviar. Inténtalo de nuevo.");
        setLoading(false);
        return;
      }
      setSent(true);
    } catch {
      setError("Error de conexión. Verifica tu internet e inténtalo de nuevo.");
    }
    setLoading(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-[#0e0528] border border-white/10 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
           style={{ animation: "promoFade 0.3s ease forwards" }}>

        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-6 py-5 relative">
          <h3 className="text-white font-extrabold text-xl leading-tight">
            🏷️ Solicitar perfil promocional
          </h3>
          <p className="text-indigo-200 text-sm mt-1">
            Cuéntanos sobre tu negocio — nos pondremos en contacto contigo
          </p>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white text-2xl leading-none font-light transition"
            aria-label="Cerrar"
          >×</button>
        </div>

        {/* Success state */}
        {sent ? (
          <div className="px-6 py-10 text-center space-y-4">
            <div className="text-6xl">✅</div>
            <h4 className="text-white font-extrabold text-xl">¡Solicitud enviada!</h4>
            <p className="text-purple-300 text-sm leading-relaxed max-w-xs mx-auto">
              Nuestro equipo revisará tu solicitud y se pondrá en contacto mediante la información que nos dejaste en un máximo de{" "}
              <span className="text-yellow-400 font-semibold">48 horas hábiles</span>.
            </p>
            <button
              onClick={onClose}
              className="mt-4 w-full bg-gradient-to-r from-indigo-600 to-purple-700 text-white font-bold py-3 rounded-xl hover:from-indigo-700 hover:to-purple-800 transition"
            >
              Entendido
            </button>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
            {error && (
              <div className="bg-red-500/20 border border-red-400/40 text-red-300 text-sm rounded-xl px-4 py-2">
                {error}
              </div>
            )}

            {/* Nombre */}
            <div>
              <label className="block text-sm font-semibold text-purple-300 mb-1">
                Nombre completo o razón social <span className="text-red-400">*</span>
              </label>
              <input
                className="w-full bg-white/10 text-white border border-white/20 focus:border-purple-500 placeholder-white/35 p-3 rounded-xl transition outline-none text-sm"
                placeholder="Ej: Papelería Central, Juan Pérez..."
                value={form.nombre}
                onChange={set("nombre")}
                required
                maxLength={100}
              />
            </div>

            {/* Contacto */}
            <div>
              <label className="block text-sm font-semibold text-purple-300 mb-1">
                Contacto preferido <span className="text-red-400">*</span>
              </label>
              <input
                className="w-full bg-white/10 text-white border border-white/20 focus:border-purple-500 placeholder-white/35 p-3 rounded-xl transition outline-none text-sm"
                placeholder="WhatsApp, teléfono, Instagram, etc."
                value={form.contacto}
                onChange={set("contacto")}
                required
                maxLength={150}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-purple-300 mb-1">
                Correo electrónico{" "}
                <span className="text-white/35 text-xs font-normal">(opcional)</span>
              </label>
              <input
                type="email"
                className="w-full bg-white/10 text-white border border-white/20 focus:border-purple-500 placeholder-white/35 p-3 rounded-xl transition outline-none text-sm"
                placeholder="tu@correo.com"
                value={form.email}
                onChange={set("email")}
                maxLength={120}
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-semibold text-purple-300 mb-1">
                ¿Por qué te interesa unirte al catálogo promocional? <span className="text-red-400">*</span>
              </label>
              <textarea
                className="w-full bg-white/10 text-white border border-white/20 focus:border-purple-500 placeholder-white/35 p-3 rounded-xl transition outline-none resize-none text-sm"
                rows={3}
                placeholder="Cuéntanos qué esperas lograr con tu perfil en SIT, a quién va dirigido tu negocio..."
                value={form.descripcion}
                onChange={set("descripcion")}
                required
                maxLength={600}
              />
              <p className="text-white/25 text-xs text-right mt-1">{form.descripcion.length}/600</p>
            </div>

            {/* Negocio */}
            <div>
              <label className="block text-sm font-semibold text-purple-300 mb-1">
                Información de tu negocio{" "}
                <span className="text-white/35 text-xs font-normal">(opcional)</span>
              </label>
              <textarea
                className="w-full bg-white/10 text-white border border-white/20 focus:border-purple-500 placeholder-white/35 p-3 rounded-xl transition outline-none resize-none text-sm"
                rows={2}
                placeholder="Tipo de negocio, productos o servicios que ofreces, tiempo en el mercado..."
                value={form.negocio}
                onChange={set("negocio")}
                maxLength={600}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-700 text-white font-bold py-3 rounded-xl hover:from-indigo-700 hover:to-purple-800 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-900/30"
            >
              {loading ? "Enviando solicitud..." : "Enviar solicitud"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Página principal ─────────────────────────────────────────────────────────
export default function PromoLanding() {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const openModal = () => setShowModal(true);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-[#08011a] via-[#0e0528] to-[#0a1628] text-white overflow-x-hidden">
      <AmbientBG />

      {/* ── Barra de navegación ──────────────────────────────────────────────── */}
      <nav className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-4 border-b border-white/10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/60 hover:text-white transition text-sm"
        >
          ← Volver
        </button>

        <div className="flex items-center gap-2">
          <span className="font-black text-xl tracking-widest bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text text-transparent">
            SIT
          </span>
          <span className="hidden sm:block text-white/30 text-xs">Social Interactive Technology</span>
        </div>

        <button
          onClick={openModal}
          className="hidden sm:flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-700 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:from-indigo-700 hover:to-purple-800 transition shadow-md shadow-purple-900/40"
        >
          Contactar ahora
        </button>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-14 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-purple-900/40 border border-purple-500/30 rounded-full px-4 py-1.5 text-sm text-purple-300 mb-7">
          <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
          Perfiles Promocionales · SIT Platform
        </div>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 tracking-tight">
          Conecta tu negocio con{" "}
          <br className="hidden sm:block" />
          <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-yellow-400 bg-clip-text text-transparent">
            cientos de estudiantes universitarios
          </span>
        </h1>

        <p className="text-lg sm:text-xl text-white/65 max-w-2xl mx-auto leading-relaxed mb-10">
          Los perfiles promocionales de SIT te dan visibilidad directa dentro de una comunidad universitaria activa y en crecimiento. Tu negocio, exactamente donde están tus futuros clientes.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={openModal}
            className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-700 text-white font-bold px-9 py-4 rounded-2xl text-lg hover:from-indigo-700 hover:to-purple-800 transition shadow-xl shadow-purple-900/40"
            style={{ animation: "promoFade 0.7s ease forwards" }}
          >
            Quiero mi perfil →
          </button>
          <a
            href="#como-funciona"
            className="text-white/50 hover:text-white/80 transition text-sm underline underline-offset-4"
          >
            ¿Cómo funciona?
          </a>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────────────────── */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pb-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: "🎓", value: "Cientos",     label: "de estudiantes activos"       },
            { icon: "📚", value: "Múltiples",   label: "carreras universitarias"      },
            { icon: "💬", value: "Activa",      label: "todos los días"               },
            { icon: "🚀", value: "100%",        label: "plataforma digital"           },
          ].map((s, i) => (
            <div
              key={i}
              className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center hover:bg-white/10 hover:border-purple-500/30 transition"
            >
              <div className="text-3xl mb-2">{s.icon}</div>
              <div className="text-2xl font-extrabold text-white">{s.value}</div>
              <div className="text-xs text-white/45 mt-1 leading-tight">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── ¿Qué es un perfil promocional? ──────────────────────────────────── */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-3">
            ¿Qué es un perfil promocional?
          </h2>
          <p className="text-white/55 max-w-xl mx-auto text-base leading-relaxed">
            Tu espacio dedicado dentro de SIT para mostrar lo que ofreces directamente a la comunidad universitaria.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon:  "📢",
              title: "Visibilidad directa",
              desc:  "Tu negocio frente a estudiantes que ya están dentro de la plataforma, activos y buscando lo que tú ofreces.",
            },
            {
              icon:  "💼",
              title: "Espacio profesional",
              desc:  "Un perfil dedicado con tu imagen de marca, descripción, publicaciones y ofertas propias, listo para impactar.",
            },
            {
              icon:  "📲",
              title: "Comunicación inmediata",
              desc:  "Los estudiantes pueden contactarte directamente desde la plataforma, sin salir de SIT ni intermediarios.",
            },
          ].map((f, i) => (
            <div
              key={i}
              className="bg-gradient-to-b from-white/10 to-white/5 border border-white/10 rounded-2xl p-7 hover:border-purple-500/40 hover:from-white/12 transition group"
            >
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform inline-block">{f.icon}</div>
              <h3 className="font-bold text-white text-lg mb-2">{f.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Cómo funciona ────────────────────────────────────────────────────── */}
      <section id="como-funciona" className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        <div className="bg-white/5 border border-white/10 rounded-3xl p-8 lg:p-14">
          <h2 className="text-3xl font-extrabold text-center mb-12">Así de simple</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { step: "01", icon: "💬", title: "Contáctanos",          desc: "Llena el formulario con la info de tu negocio. Sin compromisos ni pagos iniciales." },
              { step: "02", icon: "⚡", title: "Activamos tu perfil",  desc: "Nuestro equipo prepara tu espacio y te acompaña en los primeros pasos dentro de SIT." },
              { step: "03", icon: "🌟", title: "Conecta y crece",      desc: "Publica tus ofertas, promociones y novedades para llegar directamente a tu audiencia universitaria." },
            ].map((s, i) => (
              <div key={i} className="relative text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-700 text-2xl mb-5 shadow-lg shadow-purple-900/40">
                  {s.icon}
                </div>
                <div
                  className="absolute top-0 left-1/2 -translate-x-1/2 text-[90px] font-black leading-none pointer-events-none select-none"
                  style={{ color: "rgba(255,255,255,0.04)" }}
                >
                  {s.step}
                </div>
                <h3 className="font-bold text-white text-lg mb-2">{s.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Beneficios adicionales ───────────────────────────────────────────── */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        <h2 className="text-3xl font-extrabold text-center mb-10">¿Por qué elegir SIT para tu negocio?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { icon: "🎯", text: "Audiencia ultra-segmentada — llega exactamente a quien necesitas"        },
            { icon: "💸", text: "Planes accesibles adaptados a emprendimientos y negocios de todo tamaño" },
            { icon: "🔒", text: "Plataforma segura y verificada — solo usuarios reales universitarios"    },
            { icon: "📊", text: "Publicaciones propias con imagen, descripción y contenido personalizado" },
            { icon: "🤝", text: "Chat interno directo con cada interesado, sin apps externas"             },
            { icon: "⚡", text: "Activación rápida — tu perfil listo en poco tiempo"                     },
          ].map((b, i) => (
            <div
              key={i}
              className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl px-5 py-4 hover:bg-white/8 hover:border-indigo-500/30 transition"
            >
              <span className="text-2xl flex-shrink-0 mt-0.5">{b.icon}</span>
              <p className="text-white/75 text-sm leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Planes (teaser, sin revelar precios) ────────────────────────────── */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pb-24">
        <div className="bg-gradient-to-br from-indigo-900/60 to-purple-900/60 border border-purple-500/30 rounded-3xl p-10 lg:p-14 text-center relative overflow-hidden">
          {/* Decoración */}
          <div className="absolute -top-12 -right-12 w-52 h-52 rounded-full bg-purple-600/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-12 -left-12 w-52 h-52 rounded-full bg-indigo-600/10 blur-3xl pointer-events-none" />

          <div className="relative z-10">
            <div className="text-5xl mb-5">💎</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
              ¿Te interesan nuestros planes?
            </h2>
            <p className="text-white/65 max-w-lg mx-auto mb-3 text-lg leading-relaxed">
              Tenemos opciones cómodas y flexibles para cada tipo de negocio, desde emprendimientos hasta marcas consolidadas.
            </p>
            <p className="text-purple-300 italic text-sm mb-10">
              Contáctanos y te contamos todo lo que tenemos preparado para ti. ✨
            </p>

            <button
              onClick={openModal}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-extrabold px-9 py-4 rounded-2xl text-lg hover:from-yellow-500 hover:to-amber-600 transition shadow-xl shadow-yellow-900/30"
            >
              💬 Quiero saber los planes
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer className="relative z-10 border-t border-white/10 px-6 py-8 text-center">
        <p className="text-white/25 text-sm">
          © {new Date().getFullYear()} SIT — Social Interactive Technology
        </p>
      </footer>

      {/* Modal de contacto */}
      {showModal && <ContactModal onClose={() => setShowModal(false)} />}

      <style>{`
        @keyframes promoFade {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
      `}</style>
    </div>
  );
}
